/**
 * PdfVorschau — PDF als gerenderte Seiten statt Browser-iframe.
 *
 * Gleiche Technik wie in der Hundeverwaltung (DocumentPreviewModal): die
 * Datei wird per API als Blob geladen und mit pdf.js gezeichnet. Damit gibt
 * es keine Browser-Leiste, keinen grauen Rand und keine Download-Knöpfe im
 * Vorschaufenster, und Dark Mode wird respektiert.
 */
import React, { useEffect, useState } from 'react';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import apiClient from '../utils/apiClient';

/** Derselbe Worker wie in DocumentPreviewModal, damit nur eine Version geladen wird. */
const PDFJS_WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

interface PdfVorschauProps {
  /** API-Pfad, der das PDF liefert (mit Authorization-Header abrufbar). */
  quelle: string;
  /** Höhe des Vorschaufensters, z. B. "h-[240px] sm:h-[460px]". */
  hoeheKlasse?: string;
  /** Cache-Buster: bei Änderung wird neu geladen. */
  version?: number;
}

const PdfVorschau: React.FC<PdfVorschauProps> = ({ quelle, hoeheKlasse = 'h-[460px]', version = 0 }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  // Der Viewer bringt ein eigenes Farbschema mit; wir folgen der App.
  const dunkel = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  useEffect(() => {
    let aktiv = true;
    let url: string | null = null;
    setFehler(null);
    setBlobUrl(null);

    apiClient
      .get(quelle, { responseType: 'blob', headers: { Accept: 'application/pdf' } })
      .then(res => {
        if (!aktiv) return;
        const blob: Blob = res.data;
        if (!blob || blob.size === 0) throw new Error('leer');
        url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setBlobUrl(url);
      })
      .catch(err => {
        if (!aktiv) return;
        setFehler(err?.response?.status === 403 ? 'Keine Berechtigung' : 'PDF konnte nicht geladen werden');
      });

    return () => {
      aktiv = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [quelle, version]);

  if (fehler) {
    return (
      <div className={`flex ${hoeheKlasse} items-center justify-center text-[12px] text-gray-500 dark:text-gray-400`}>
        {fehler}
      </div>
    );
  }
  if (!blobUrl) {
    return <div className={`${hoeheKlasse} w-full animate-pulse bg-gray-100 dark:bg-gray-800`} aria-hidden="true" />;
  }
  return (
    <Worker workerUrl={PDFJS_WORKER_URL}>
      <div className={`${hoeheKlasse} w-full overflow-auto`}>
        <Viewer fileUrl={blobUrl} defaultScale={SpecialZoomLevel.PageWidth} theme={dunkel ? 'dark' : 'light'} />
      </div>
    </Worker>
  );
};

export default PdfVorschau;
